import {
    FieldErrors,
    FieldValues,
    FormProvider,
    SubmitHandler,
    useForm,
    UseFormProps,
    UseFormReturn,
    useFormState,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { PropsWithChildren, useEffect } from 'react';
import * as Yup from 'yup';

export interface FormContainerProps<T extends FieldValues> extends PropsWithChildren<UseFormProps<T>> {
    onSubmit: SubmitHandler<T>;
    schema?: Yup.AnyObjectSchema;
    formContext?: UseFormReturn<T>;
    onDirty?: (val: boolean, dirtyFields?: Record<any, any>) => void;
    className?: string;
}

const FormContainer = <T extends FieldValues = FieldValues>({
    schema,
    children,
    onSubmit,
    className,
    formContext,
    onDirty,
    ...useFormProps
}: PropsWithChildren<FormContainerProps<T>>) => {
    const onError = (errors: FieldErrors) => {
        const errorsArray = Object.keys(errors);
        if (errorsArray.length) {
            const nodeList = document.getElementsByName(errorsArray[0]);
            if (nodeList && nodeList.length) {
                nodeList[0].scrollIntoView({ behavior: 'smooth' });
                nodeList[0].focus({ preventScroll: true });
            }
        }
    };

    if (!formContext) {
        const methods = useForm<T>({
            mode: 'onChange',
            reValidateMode: 'onBlur',
            resolver: schema && yupResolver(schema),
            ...useFormProps,
        });
        const { handleSubmit, control } = methods;

        const { isDirty } = useFormState({ control });

        useEffect(() => {
            onDirty?.(isDirty);
        }, [isDirty]);

        return (
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit, onError)} className={className} noValidate>
                    {children}
                </form>
            </FormProvider>
        );
    } else {
        const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            event.stopPropagation();
            return formContext.handleSubmit(
                (values) => {
                    onSubmit(values);
                },
                (errors) => {
                    onError(errors);
                },
            )(event);
        };
        return (
            <FormProvider {...formContext}>
                <form className={className} onSubmit={handleSubmit} noValidate>
                    {children}
                </form>
            </FormProvider>
        );
    }
};

export default FormContainer;
